import React from 'react'
import styled from 'styled-components'
import { LandUsePlanDocument } from '@popety-io/types'
import { Icon } from '../../../../../atoms/Icon'

const ListItem = styled.button<{ $isSelected: boolean }>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.primary + '10' : 'transparent'};
  border: none;
  border-left: 3px solid
    ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`

const DocumentIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme }) => theme.colors.primary}10;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
  margin-top: 2px;
`

const DocumentContent = styled.div`
  flex: 1;
  min-width: 0;
`

const DocumentTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: ${({ theme }) => theme.spacing.micro};
`

const DocumentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

const ImportantBadge = styled.span`
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme }) => theme.colors.warning}20;
  color: ${({ theme }) => theme.colors.warning};
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
`

const Separator = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export interface DocumentListItemTranslations {
  documentTypes?: {
    rule?: string
    map?: string
  }
  important?: string
}

const DEFAULT_TRANSLATIONS: DocumentListItemTranslations = {
  documentTypes: {
    rule: 'rule',
    map: 'map',
  },
  important: 'Important',
}

interface DocumentListItemProps {
  document: LandUsePlanDocument
  isSelected: boolean
  onClick: () => void
  translations?: DocumentListItemTranslations
  locale?: string
}

const getDocumentIcon = (docType?: string): string => {
  switch (docType) {
    case 'rule':
      return 'file-text'
    case 'map':
      return 'map'
    default:
      return 'file'
  }
}

const formatDate = (dateString?: string, locale?: string): string => {
  if (!dateString) return ''
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString(locale || 'fr-CH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return ''
  }
}

export const DocumentListItem: React.FC<DocumentListItemProps> = ({
  document,
  isSelected,
  onClick,
  translations,
  locale,
}) => {
  const t = {
    ...DEFAULT_TRANSLATIONS,
    ...translations,
    documentTypes: {
      ...DEFAULT_TRANSLATIONS.documentTypes,
      ...translations?.documentTypes,
    },
  }

  const iconName = getDocumentIcon(document.document_type)

  // Translate document type
  const getTranslatedDocumentType = (docType?: string): string => {
    if (!docType) return ''
    return t.documentTypes?.[docType as keyof typeof t.documentTypes] || docType
  }

  const metaParts = []
  if (document.document_type) {
    metaParts.push(getTranslatedDocumentType(document.document_type))
  }
  if (document.publication_date) {
    metaParts.push(formatDate(document.publication_date, locale))
  }
  if (document.language) {
    metaParts.push(document.language.toUpperCase())
  }

  return (
    <ListItem onClick={onClick} $isSelected={isSelected} type="button">
      <DocumentIconWrapper>
        <Icon name={iconName as any} size="small" />
      </DocumentIconWrapper>

      <DocumentContent>
        <DocumentTitle>
          {document.title}
          {document.important && (
            <ImportantBadge style={{ marginLeft: '8px' }}>
              {t.important}
            </ImportantBadge>
          )}
        </DocumentTitle>
        {metaParts.length > 0 && (
          <DocumentMeta>
            {metaParts.map((part, index) => (
              <React.Fragment key={index}>
                {index > 0 && <Separator>•</Separator>}
                <span>{part}</span>
              </React.Fragment>
            ))}
          </DocumentMeta>
        )}
      </DocumentContent>
    </ListItem>
  )
}
